import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_SITE } from './siteDefaults';
import { resolveAbout } from './siteAbout';
import { resolveHeader } from './siteHeader';
import { resolveSiteSeo } from './siteSeo';
import DocumentHead from './DocumentHead';
import SiteFooter from './components/SiteFooter';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import { apiUrl } from './apiBase';

const FALLBACK_HERO =
  'https://res.cloudinary.com/dnw8qhp3h/image/upload/v1777804247/rafid-seed/admin/u2cu0nlo55bxxufhxrmf.jpg';

function CheckBadge({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.4" className="text-brand-lime" />
      <path
        d="M6 10l2.5 2.5L14 7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-brand-lime"
      />
    </svg>
  );
}

function MapPinHouseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 56" fill="none" aria-hidden>
      <path
        d="M24 0C12.54 0 3.5 9.04 3.5 20.2c0 14.35 17.85 33.55 19.65 35.55a2 2 0 002.7 0C27.65 53.75 44.5 34.55 44.5 20.2 44.5 9.04 35.46 0 24 0z"
        fill="#dc2626"
      />
      <path
        d="M24 8c-5.52 0-10 4.48-10 10 0 6.5 10 16 10 16s10-9.5 10-16c0-5.52-4.48-10-10-10z"
        fill="#fff"
        fillOpacity={0.95}
      />
      <path
        d="M19.5 22.5V17h9v5.5l-4.5 3.5-4.5-3.5z"
        stroke="#14261c"
        strokeWidth={1.2}
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function OurStorySection({ journey }) {
  const accentSrc = journey.accentImageUrl?.trim();
  return (
    <section className="relative mb-14 sm:mb-16 lg:mb-20" aria-labelledby="our-story-heading">
      <div className="relative overflow-hidden rounded-3xl bg-neutral-100 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.12)] ring-1 ring-black/[0.06]">
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-stretch">
          <div className="relative hover_effect_image min-h-[280px] sm:min-h-[320px] lg:min-h-[420px]">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${journey.visualUrl})` }}
              aria-hidden
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-brand-forest/25 via-transparent to-brand-forest/40"
              aria-hidden
            />
            <div className="absolute inset-0 flex items-center justify-center pb-8 pt-12">
              <div className="relative flex flex-col items-center">
                <div
                  className="relative h-24 w-36 rounded-lg bg-gradient-to-b from-amber-100/90 to-emerald-800/90 shadow-[0_12px_24px_rgba(0,0,0,0.35)] ring-2 ring-white/40 sm:h-28 sm:w-40"
                  aria-hidden
                />
                <div className="-mt-3 drop-shadow-lg">
                  <MapPinHouseIcon className="h-14 w-12 sm:h-16 sm:w-14" />
                </div>
                <div
                  className="absolute -bottom-2 left-1/2 h-3 w-20 -translate-x-1/2 rounded-[100%] bg-black/25 blur-sm"
                  aria-hidden
                />
              </div>
            </div>
            {journey.timelineYears.length > 0 ? (
              <div
                className="absolute bottom-0 right-0 top-0 flex w-12 flex-col justify-between py-10 pr-2 text-right sm:w-14 sm:py-12 sm:pr-3"
                aria-hidden
              >
                {journey.timelineYears.map((y) => (
                  <span key={y} className="text-sm font-bold tabular-nums text-white drop-shadow-md sm:text-base">
                    {y}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="relative bg-[#f4f6f4] px-6 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12">
            {accentSrc ? (
              <img
                src={accentSrc}
                alt=""
                className="pointer-events-none absolute -right-2 -top-4 z-10 hidden w-24 -rotate-12 rounded-2xl object-cover shadow-lg ring-2 ring-white sm:block sm:w-28 lg:-right-4 lg:-top-6 lg:w-32"
                loading="lazy"
                decoding="async"
              />
            ) : null}

            <p className="mb-3 inline-block rounded-md bg-brand-lime/25 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-brand-forest sm:text-xs">
              {journey.sectionLabel}
            </p>
            <h2
              id="our-story-heading"
              className="relative z-[1] m-0 max-w-lg text-2xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-3xl lg:text-[1.85rem] lg:leading-snug"
            >
              {journey.title}
            </h2>

            <p className="relative z-[1] m-0 mt-5 max-w-xl text-sm leading-relaxed text-neutral-600 sm:mt-6 sm:text-[0.95rem]">
              {journey.body}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionTargetIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden>
      <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.5" className="text-brand-lime/80" />
      <circle cx="16" cy="16" r="8" stroke="currentColor" strokeWidth="1.5" className="text-brand-forest/70" />
      <circle cx="16" cy="16" r="2.5" fill="currentColor" className="text-brand-forest" />
    </svg>
  );
}

function VisionHorizonIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden>
      <path
        d="M4 22h24M4 22c4-6 8-9 12-9s8 3 12 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-brand-forest/75"
      />
      <path
        d="M8 10a8 8 0 0116 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className="text-brand-lime"
      />
    </svg>
  );
}

function MissionVisionSection({ missionVision }) {
  return (
    <section className="relative mb-14 sm:mb-16 lg:mb-20" aria-labelledby="mission-vision-heading">
      <div className="relative overflow-hidden rounded-3xl bg-brand-forest text-white shadow-[0_20px_50px_-12px_rgba(20,38,28,0.35)] ring-1 ring-white/10">
        <div
          className="pointer-events-none absolute -right-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-brand-lime/20 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-20 bottom-0 h-56 w-56 rounded-full bg-white/5 blur-3xl"
          aria-hidden
        />

        <div className="relative px-6 py-10 sm:px-8 sm:py-12 lg:px-12 lg:py-14">
          <header className="max-w-3xl">
            <p className="mb-3 inline-block rounded-md bg-white/15 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-brand-lime-soft sm:text-xs">
              {missionVision.sectionEyebrow}
            </p>
            <h2
              id="mission-vision-heading"
              className="m-0 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-[1.85rem] lg:leading-snug"
            >
              {missionVision.sectionTitle}
            </h2>
          </header>

          <div className="mt-10 grid gap-8 lg:mt-12 lg:grid-cols-2 lg:gap-10 lg:gap-x-12">
            <article className="relative rounded-2xl border border-white/15 bg-white/[0.07] p-6 backdrop-blur-sm sm:p-8">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-lime/20 text-brand-lime">
                <MissionTargetIcon className="h-6 w-6" />
              </div>
              <h3 className="m-0 text-lg font-bold text-white sm:text-xl">{missionVision.missionTitle}</h3>
              <p className="m-0 mt-3 text-sm leading-relaxed text-white/85 sm:text-[0.95rem]">{missionVision.missionBody}</p>
            </article>

            <article className="relative rounded-2xl border border-white/15 bg-white/[0.07] p-6 backdrop-blur-sm sm:p-8">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-lime/20 text-brand-lime">
                <VisionHorizonIcon className="h-6 w-6" />
              </div>
              <h3 className="m-0 text-lg font-bold text-white sm:text-xl">{missionVision.visionTitle}</h3>
              <p className="m-0 mt-3 text-sm leading-relaxed text-white/85 sm:text-[0.95rem]">{missionVision.visionBody}</p>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

function ManagementPanelSection({ managementPanel }) {
  if (!managementPanel.members.length) return null;

  return (
    <section className="relative mt-14 sm:mt-16 lg:mt-20" aria-labelledby="management-panel-heading">
      <div className="relative overflow-hidden rounded-3xl border border-neutral-200/80 bg-gradient-to-b from-[#f7faf7] to-white px-6 py-10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04] sm:px-8 sm:py-12 lg:px-10 lg:py-14">
        <div
          className="pointer-events-none absolute -right-20 top-0 h-56 w-56 rounded-full bg-brand-lime/15 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-brand-forest/10 blur-3xl"
          aria-hidden
        />

        <header className="relative max-w-3xl">
          <p className="mb-3 inline-block rounded-md bg-brand-lime/25 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-brand-forest sm:text-xs">
            {managementPanel.sectionEyebrow}
          </p>
          <h2
            id="management-panel-heading"
            className="m-0 text-2xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-3xl lg:text-[1.85rem] lg:leading-snug"
          >
            {managementPanel.sectionTitle}
          </h2>
          <p className="m-0 mt-4 max-w-2xl text-sm leading-relaxed text-neutral-600 sm:text-[0.95rem]">
            {managementPanel.intro}
          </p>
        </header>

        <ul className="relative m-0 mt-10 grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:mt-12 lg:grid-cols-4 lg:gap-8">
          {managementPanel.members.map((member) => (
            <li key={member.id}>
              <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(20,38,28,0.18)]">
                <div className="relative aspect-[4/5] overflow-hidden bg-neutral-100">
                  <img
                    src={member.image}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                    decoding="async"
                  />
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-forest/35 via-transparent to-transparent opacity-80"
                    aria-hidden
                  />
                </div>
                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <h3 className="m-0 text-lg font-bold leading-snug text-brand-forest">{member.name}</h3>
                  <p className="m-0 mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-brand-lime sm:text-[0.7rem]">
                    {member.role}
                  </p>
                  <p className="m-0 mt-3 flex-1 text-sm leading-relaxed text-neutral-600">{member.bio}</p>
                </div>
              </article>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));

  const about = useMemo(() => resolveAbout(site), [site]);
  const headerSection = useMemo(() => resolveHeader(site), [site]);
  const baseSeo = useMemo(() => resolveSiteSeo(site), [site]);

  const pageSeo = useMemo(() => {
    const brand = `${headerSection.brandPrimary} ${headerSection.brandSecondary}`.trim() || 'Rafid Seed';
    return {
      ...baseSeo,
      htmlTitle: `About Us | ${brand}`,
      socialTitle: `About Us — ${baseSeo.socialTitle || brand}`,
      canonicalUrl: typeof window !== 'undefined' ? `${window.location.origin}/about` : '',
    };
  }, [baseSeo, headerSection.brandPrimary, headerSection.brandSecondary]);

  const heroImage = about.pageHeroImage?.trim() || about.videoPosterUrl?.trim() || FALLBACK_HERO;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unreachable'))))
      .then(setSite)
      .catch(() => { });
  }, []);

  const heading =
    [about.headingLine1, about.headingLine2].filter(Boolean).join(' ').trim() ||
    'Building better solutions for a smarter future';

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <DocumentHead seo={pageSeo} />

      <MarketingSiteHeader
        site={site}
        idPrefix="about"
        isNavLinkCurrent={(link, path) => {
          const { href, current } = link;
          const isAboutLink = href === '/about' || href === '#about';
          return Boolean(current) || (isAboutLink && path === '/about');
        }}
      />

      <section className="relative isolate min-h-[min(26vh,240px)] overflow-hidden pt-[calc(3.85rem+env(safe-area-inset-top))] sm:min-h-[min(30vh,280px)] sm:pt-[calc(4.25rem+env(safe-area-inset-top))] md:pt-[calc(4.5rem+env(safe-area-inset-top))] pb-0">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-b " aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-brand-forest/85 to-transparent" aria-hidden />
        <div className="relative z-[1] mx-auto max-w-shell px-4 mt-40">
          <div className="relative">

            {/* MAIN SHAPE */}
            <div className="relative bg-[#e8f5e9] text-center
      rounded-t-[60px] px-6 pt-10 pb-10
      shadow-[0_-20px_60px_rgba(0,0,0,0.15)] overflow-hidden">

              {/* LEFT CUT */}
              <div className="absolute -left-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" />

              {/* RIGHT CUT */}
              <div className="absolute -right-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" />

              {/* CONTENT */}
              <h1 className="text-3xl font-bold text-brand-forest">
                About Us
              </h1>

              <p className="mt-3 flex justify-center gap-2 text-xs uppercase text-brand-lime">
                HOME :
                <span className="text-brand-forest">ABOUT US</span>
              </p>

            </div>
          </div>
        </div>
      </section>

      <main className="relative bg-white">
        <div className="mx-auto max-w-shell px-4 py-14 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:py-24">
          <OurStorySection journey={about.journey} />
          <MissionVisionSection missionVision={about.missionVision} />
          <ManagementPanelSection managementPanel={about.managementPanel} />
        </div>
      </main>

      <SiteFooter site={site} headerSection={headerSection} />
    </div>
  );
}
